
/**
 * 品項模組 - 型別定義
 * Equipment Item Module - Type Definitions
 * 
 * 對應資料庫表：equipment_items
 * 
 * ============================================
 * 品項識別規則
 * ============================================
 * 
 * 品項使用 UUID 作為主鍵（核心業務資料）
 * - 品項是實體資產，需要長期追蹤
 * - 關聯訂單、客戶、位置等多個表
 * - UUID 確保資料安全性和歷史記錄完整性
 * 
 * 序號格式（serial_number）：
 * - 由使用者自訂或使用原廠序號
 * - 格式範例：FX6-TW-001、ARRI-MLF-001
 * - 每台機器的唯一識別碼
 * 
 * ============================================
 */

/**
 * 品項狀態
 * 對應資料庫 CHECK 約束
 */
export type EquipmentItemStatus = 'available' | 'rented' | 'maintenance' | 'retired';

/**
 * 品項狀態常數
 */
export const EQUIPMENT_ITEM_STATUS = {
  AVAILABLE: 'available' as const,
  RENTED: 'rented' as const,
  MAINTENANCE: 'maintenance' as const,
  RETIRED: 'retired' as const,
};

/**
 * 品項狀態配置
 */
export interface EquipmentItemStatusConfig {
  id: EquipmentItemStatus;
  name: string;
  nameEn: string;
  color: string;
  textColor: string;
  bgLight: string;
  icon: string;
  description: string;
}

/**
 * 品項狀態配置列表
 */
export const EQUIPMENT_ITEM_STATUS_CONFIG: EquipmentItemStatusConfig[] = [
  {
    id: 'available',
    name: '在庫',
    nameEn: 'Available',
    color: 'bg-green-500',
    textColor: 'text-green-700',
    bgLight: 'bg-green-50',
    icon: 'ri-check-line',
    description: '可供租借'
  },
  {
    id: 'rented',
    name: '出機',
    nameEn: 'Rented',
    color: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgLight: 'bg-blue-50',
    icon: 'ri-shopping-cart-line',
    description: '已租出'
  },
  {
    id: 'maintenance',
    name: '維修',
    nameEn: 'Maintenance',
    color: 'bg-orange-500',
    textColor: 'text-orange-700',
    bgLight: 'bg-orange-50',
    icon: 'ri-tools-line',
    description: '維修中'
  },
  {
    id: 'retired',
    name: '報廢/處分',
    nameEn: 'Retired',
    color: 'bg-gray-500',
    textColor: 'text-gray-700',
    bgLight: 'bg-gray-100',
    icon: 'ri-delete-bin-line',
    description: '已報廢或處分'
  }
];

/**
 * 品項介面
 * 對應 equipment_items 表
 */
export interface EquipmentItem {
  /** UUID 主鍵 */
  id: string;
  /** 產品 ID（關聯 products 表） */
  product_id: string;
  /** 出廠序號（唯一識別碼） */
  serial_number: string;
  /** 所在門市 ID（關聯 locations 表） */
  location_id: string | null;
  /** 品項狀態 */
  status: EquipmentItemStatus;
  /** 購入日期 */
  purchase_date: string | null;
  /** 購入價格（新台幣） */
  purchase_price: number | null;
  /** 備註 */
  notes: string | null;
  /** 建立時間 */
  created_at: string;
  /** 更新時間 */
  updated_at: string;
}

/**
 * 品項（含關聯資料）
 */
export interface EquipmentItemWithRelations extends EquipmentItem {
  /** 關聯的產品資料 */
  product?: {
    id: string;
    name_zh: string;
    name_ja?: string;
    brand: string;
    category: string;
    status: string;
    daily_price: number;
    image_urls?: string[];
  };
  /** 關聯的門市資料 */
  location?: {
    id: string;
    name_zh: string;
    name_ja?: string;
    type: string;
  };
}

/**
 * 新增品項輸入
 */
export interface EquipmentItemCreateInput {
  product_id: string;
  serial_number: string;
  location_id?: string | null;
  status?: EquipmentItemStatus;
  purchase_date?: string | null;
  purchase_price?: number | null;
  notes?: string | null;
}

/**
 * 更新品項輸入
 */
export interface EquipmentItemUpdateInput {
  product_id?: string;
  serial_number?: string;
  location_id?: string | null;
  status?: EquipmentItemStatus;
  purchase_date?: string | null;
  purchase_price?: number | null;
  notes?: string | null;
}

/**
 * 品項統計
 */
export interface EquipmentItemStats {
  /** 總數 */
  total: number;
  /** 在庫數量 */
  available: number;
  /** 出機數量 */
  rented: number;
  /** 維修數量 */
  maintenance: number;
  /** 報廢數量 */
  retired: number;
}

/**
 * 品項篩選條件
 */
export interface EquipmentItemFilter {
  /** 產品 ID */
  productId?: string;
  /** 門市 ID */
  locationId?: string;
  /** 狀態 */
  status?: EquipmentItemStatus;
  /** 搜尋關鍵字（序號、產品名稱） */
  search?: string;
}

/**
 * 品項歷史記錄動作類型
 */
export type EquipmentItemHistoryAction = 
  | 'created'
  | 'status_change'
  | 'location_transfer'
  | 'info_update'
  | 'order_rented'
  | 'order_returned'
  | 'maintenance_start'
  | 'maintenance_end'
  | 'retired'
  | 'note_added';

/**
 * 品項歷史記錄動作配置
 */
export interface EquipmentItemHistoryActionConfig {
  action: EquipmentItemHistoryAction;
  label: string;
  icon: string;
  color: string;
}

/**
 * 品項歷史記錄動作配置列表
 */
export const EQUIPMENT_ITEM_HISTORY_ACTIONS: EquipmentItemHistoryActionConfig[] = [
  { action: 'created', label: '建立品項', icon: 'ri-add-circle-line', color: 'text-green-600 bg-green-50' },
  { action: 'status_change', label: '狀態變更', icon: 'ri-refresh-line', color: 'text-blue-600 bg-blue-50' },
  { action: 'location_transfer', label: '門市調動', icon: 'ri-map-pin-line', color: 'text-purple-600 bg-purple-50' },
  { action: 'info_update', label: '資訊更新', icon: 'ri-edit-line', color: 'text-orange-600 bg-orange-50' },
  { action: 'order_rented', label: '出租', icon: 'ri-shopping-cart-line', color: 'text-cyan-600 bg-cyan-50' },
  { action: 'order_returned', label: '歸還', icon: 'ri-inbox-archive-line', color: 'text-teal-600 bg-teal-50' },
  { action: 'maintenance_start', label: '送修', icon: 'ri-tools-line', color: 'text-amber-600 bg-amber-50' },
  { action: 'maintenance_end', label: '維修完成', icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
  { action: 'retired', label: '報廢處分', icon: 'ri-delete-bin-line', color: 'text-red-600 bg-red-50' },
  { action: 'note_added', label: '新增備註', icon: 'ri-sticky-note-line', color: 'text-gray-600 bg-gray-100' }
];

/**
 * 品項歷史記錄
 */
export interface EquipmentItemHistory {
  id: string;
  item_id: string;
  action_type: EquipmentItemHistoryAction;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  old_location_id: string | null;
  new_location_id: string | null;
  related_order_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  /** 關聯的舊門市 */
  old_location?: {
    id: string;
    name_zh: string;
  };
  /** 關聯的新門市 */
  new_location?: {
    id: string;
    name_zh: string;
  };
}

/**
 * 批次新增品項輸入
 */
export interface EquipmentItemBatchCreateInput {
  product_id: string;
  location_id?: string | null;
  /** 手動輸入的序號列表 */
  serial_numbers?: string[];
  /** 自動產生序號的前綴 */
  prefix?: string;
  /** 自動產生序號的起始編號 */
  startNumber?: number;
  /** 自動產生的數量 */
  quantity?: number;
}

/**
 * 品項匯入資料
 */
export interface EquipmentItemImportData {
  _index: number;
  _productName: string;
  _locationName: string;
  product_id: string | null;
  serial_number: string;
  location_id: string | null;
  status: EquipmentItemStatus;
  statusText: string;
  purchase_date: string | null;
  purchase_price: number | null;
  notes: string;
}
